import styled, { css, keyframes } from "styled-components";

export const DESKTOP_BREAKPOINT = "1024px";
export const PREVIEW_BREAKPOINT = "1280px";
const SIDEBAR_WIDTH = "290px";
const PREVIEW_WIDTH = "460px";
const BOTTOM_NAV_HEIGHT = "64px";
// Gap kept around the floating mobile nav (sides, bottom, and above it for fixed footers)
const BOTTOM_NAV_GAP = "20px";

type ShellProps = {
  $withBottomNav: boolean;
  $withPreview: boolean;
};

export const Shell = styled.div<ShellProps>`
  --app-sidebar-width: 0px;
  --app-preview-width: 0px;
  /* Space the floating nav takes from the bottom of the screen, gaps included */
  --app-bottom-nav-height: ${props =>
    props.$withBottomNav ? `calc(${BOTTOM_NAV_HEIGHT} + 2 * ${BOTTOM_NAV_GAP} + env(safe-area-inset-bottom))` : "0px"};
  /* Where the floating nav's top edge is: a screen's fixed action bar sits right on it, no gap between */
  --app-bottom-bar-offset: ${props =>
    props.$withBottomNav ? `calc(${BOTTOM_NAV_HEIGHT} + ${BOTTOM_NAV_GAP} + env(safe-area-inset-bottom))` : "0px"};

  display: flex;
  min-height: 100vh;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    --app-sidebar-width: ${SIDEBAR_WIDTH};
    --app-bottom-nav-height: 0px;
    --app-bottom-bar-offset: 0px;
  }

  ${props =>
    props.$withPreview &&
    css`
      @media (min-width: ${PREVIEW_BREAKPOINT}) {
        --app-preview-width: ${PREVIEW_WIDTH};
      }
    `}
`;

export const Main = styled.main`
  flex: 1;
  min-width: 0;
  padding-bottom: var(--app-bottom-nav-height);
`;

export const Sidebar = styled.aside`
  display: none;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: var(--app-sidebar-width);
    height: 100vh;
    padding: 0 1rem;
    /* the header and the footer (Ajuda, Feedback, Conta) always show; only the menu in between scrolls */
    overflow: hidden;
    background: var(--color-neutral-darkness);
    border-right: 1px solid var(--color-neutral-dark);
  }
`;

/* Sidebar blocks span the full sidebar width so their borders reach both edges */
export const SidebarHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 0.75rem;
  align-items: center;
  padding: 1.5rem 1.75rem;
  margin: 0 -1rem;
  border-bottom: 1px solid var(--color-neutral-dark);
`;

export const Logo = styled.button`
  display: flex;
  flex-shrink: 0;
  align-items: center;

  /* The full wordmark (77px wide): its width follows the height */
  svg {
    width: auto;
    height: 25px;
  }
`;

/* "Olá, Fulano 👋" on the logo's row, pushed to the right */
export const Greeting = styled.p`
  display: flex;
  gap: 0.25rem;
  min-width: 0;
  margin-left: auto;
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--color-neutral-150);
  white-space: nowrap;

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const SidebarBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
  padding: 1.5rem 0;
  overflow-y: auto;
`;

export const NavGroup = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  & + & {
    margin-top: 1.5rem;
  }
`;

/** The title of a group of the menu (e.g. "Tools"): small, as the labels of the panel, in line with the items. */
export const NavGroupTitle = styled.span`
  padding: 0 0.75rem 0.25rem;
  color: var(--color-neutral-light);
  font-family: Inter, sans-serif;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1rem;
  letter-spacing: 0.03125rem;
  text-transform: uppercase;
`;

type NavItemProps = {
  $active?: boolean;
  /** A group's parent with one of its subitems open: white text, no background (the subitem has it). */
  $parentActive?: boolean;
  /** A subitem: no icon, its text lined up with the parent's label. */
  $sub?: boolean;
};

export const NavItem = styled.button<NavItemProps>`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  padding: 0.625rem 0.75rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  color: ${props => (props.$active || props.$parentActive ? "var(--color-neutral-white)" : "var(--color-neutral-150)")};
  text-align: left;
  background: ${props => (props.$active ? "var(--color-neutral-dark)" : "transparent")};
  border-radius: 8px;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    color: var(--color-neutral-white);
    background: var(--color-neutral-dark);
  }

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }

  .plus {
    color: var(--color-brand-plus);
  }

  /* Past the parent's icon (20px) and gap */
  ${props =>
    props.$sub &&
    css`
      padding-top: 0.4375rem;
      padding-bottom: 0.4375rem;
      padding-left: 2.75rem;
      font-size: 0.8125rem;
      font-weight: 400;
    `}
`;

/* Pushes the PLUS block to the bottom of the sidebar body */
export const SidebarPlusPromo = styled.div`
  margin-top: auto;

  /* a short screen: the menu and the account come first */
  @media (max-height: 820px) {
    display: none;
  }
`;

export const SidebarFooter = styled.div`
  flex-shrink: 0;
  padding: 1rem;
  margin: 0 -1rem;
  border-top: 1px solid var(--color-neutral-dark);
`;

/*
 * Small sign-off under the footer menu, aligned with the menu items' icons. Its top border spans
 * the whole sidebar, like the other separators.
 */
export const MadeInBrazil = styled.p`
  display: flex;
  gap: 0.375rem;
  align-items: center;
  padding: 1rem 1.75rem 0;
  margin: 1rem -1rem 0;
  border-top: 1px solid var(--color-neutral-dark);
  font-family: Inter, sans-serif;
  font-size: 0.6875rem;
  line-height: 1rem;
  color: var(--color-neutral-150);
  opacity: 0.7;

  svg {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
  }
`;

export const BottomNav = styled.nav`
  position: fixed;
  right: ${BOTTOM_NAV_GAP};
  bottom: calc(${BOTTOM_NAV_GAP} + env(safe-area-inset-bottom));
  left: ${BOTTOM_NAV_GAP};
  z-index: 100;
  display: flex;
  height: ${BOTTOM_NAV_HEIGHT};
  padding: 0 0.375rem;
  background: var(--color-neutral-dark);
  border: 1px solid var(--color-neutral-base);
  border-radius: 20px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    display: none;
  }
`;

export const BottomNavItem = styled.button<NavItemProps>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;
  justify-content: center;
  min-width: 0;
  margin: 0.375rem 0;
  font-family: Inter, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 0.875rem;
  color: ${props => (props.$active ? "var(--color-neutral-white)" : "var(--color-neutral-150)")};
  background: ${props => (props.$active ? "var(--color-brand-blue)" : "transparent")};
  border-radius: 14px;
  transition:
    background 0.15s,
    color 0.15s;

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .label {
    max-width: 100%;
    padding: 0 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/* Something new to see (Novidades): a dot on the corner of the entry's icon, and of "More" when it's inside it */
export const NewDot = styled.span`
  position: absolute;
  top: -2px;
  right: -3px;
  width: 8px;
  height: 8px;
  /* the brand's green, as the "Lançamento" tag on the list */
  background: var(--color-lemon);
  border-radius: 999px;
  box-shadow: 0 0 0 2px var(--color-neutral-darkness);
`;

/* Dims the screen behind the "More" panel; under the bar, so the bar's own button still closes it */
export const BottomMoreBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99;
  background: rgba(0, 0, 0, 0.5);

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    display: none;
  }
`;

const moreOpen = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
`;

/* The panel of "More": a card of the bar's own skin that grows from right above it */
export const BottomMoreSheet = styled.nav`
  position: fixed;
  right: ${BOTTOM_NAV_GAP};
  bottom: calc(${BOTTOM_NAV_HEIGHT} + ${BOTTOM_NAV_GAP} + 12px + env(safe-area-inset-bottom));
  left: ${BOTTOM_NAV_GAP};
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: calc(100vh - ${BOTTOM_NAV_HEIGHT} - 2 * ${BOTTOM_NAV_GAP} - 12px - env(safe-area-inset-bottom) - 20px);
  padding: 0.5rem;
  overflow-y: auto;
  background: var(--color-neutral-dark);
  border: 1px solid var(--color-neutral-base);
  border-radius: 20px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
  transform-origin: bottom right;
  animation: ${moreOpen} 0.2s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  /* Tablets: a card over the "More" button instead of the whole width */
  @media (min-width: 640px) {
    left: auto;
    width: 360px;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    display: none;
  }

  /* On this background the rows' own highlight would vanish: the bar's blue marks the screen, as in the bar */
  button[aria-current="page"] {
    background: var(--color-brand-blue);
  }
`;

/* "Olá, Fulano 👋" at the top of the panel: it also takes to the account */
export const BottomMoreHeader = styled.button`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  width: 100%;
  padding: 0.625rem 0.75rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  color: var(--color-neutral-white);
  text-align: left;
  border-radius: 8px;

  .name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const BottomMoreDivider = styled.hr`
  height: 1px;
  margin: 0.25rem 0.75rem;
  background: var(--color-neutral-base);
  border: 0;
`;

/* Arriving from the creation steps: the column opens from zero width (the content narrows along) */
const previewArrive = keyframes`
  from {
    width: 0;
  }
`;

/* The phone column keeps its final width inside and is anchored right, so while the column opens it
   slides in from the right edge instead of being resized */
export const PreviewAside = styled.aside<{ $arriving?: boolean }>`
  display: none;

  @media (min-width: ${PREVIEW_BREAKPOINT}) {
    position: sticky;
    top: 0;
    display: flex;
    flex-shrink: 0;
    justify-content: flex-end;
    width: var(--app-preview-width);
    height: 100vh;
    overflow: hidden;
    border-left: 1px solid var(--color-neutral-dark);

    ${props =>
      props.$arriving &&
      css`
        animation: ${previewArrive} 0.5s cubic-bezier(0.22, 1, 0.36, 1);

        @media (prefers-reduced-motion: reduce) {
          animation: none;
        }
      `}
  }

  .inner {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: calc(var(--app-preview-width) - 1px);
    height: 100%;
    padding: 2rem;
  }
`;
